/**
 * Sonido de la campana de propinas: una campana sintetizada con Web Audio (sin
 * ficheros) y después la frase.
 *
 * La frase sale, por orden:
 *  1. de un audio grabado, si existe: public/campana/audio/<idioma>/<clave>.mp3
 *     (clave = la frase, p. ej. gracias_equipo, o "propina" sin frase);
 *  2. si no, de la voz del dispositivo (speechSynthesis), en el idioma del
 *     negocio si tiene voz para él; si no, en castellano o inglés; y si no
 *     tiene ninguna, con la que haya ("como pueda").
 */

const VOCES = { es: "es-ES", ca: "ca-ES", en: "en-GB" };
/** Si el dispositivo no tiene voz para el idioma del negocio, se prueba con estos. */
const IDIOMAS_RESPALDO = ["es", "en"];

const FRASES = {
  es: {
    propina: "¡Propina para el equipo!",
    gracias_equipo: "¡Gracias, equipo!",
    todo_buenisimo: "¡Todo buenísimo!",
    volveremos: "¡Volveremos!",
    sois_geniales: "¡Sois geniales!",
  },
  ca: {
    propina: "Propina per a l'equip!",
    gracias_equipo: "Gràcies, equip!",
    todo_buenisimo: "Tot boníssim!",
    volveremos: "Tornarem!",
    sois_geniales: "Sou genials!",
  },
  en: {
    propina: "A tip for the team!",
    gracias_equipo: "Thanks, team!",
    todo_buenisimo: "Everything was great!",
    volveremos: "We'll be back!",
    sois_geniales: "You're amazing!",
  },
};

const frases = (idioma) => FRASES[idioma] || FRASES.es;

/** Texto de la frase rápida en el idioma dado (para enseñarla en pantalla). */
export const fraseCampana = (frase, idioma) => (frase ? frases(idioma)[frase] || null : null);

/** Lo que se dice por un toque: "¡Propina para el equipo!" y la frase, si la hay. */
export function textoCampana(evento, idioma) {
  const f = frases(idioma);
  return [f.propina, evento.frase ? f[evento.frase] : null].filter(Boolean).join(" ");
}

let audioCtx = null;

/** Hay que llamarlo desde un toque del usuario: los navegadores bloquean el audio hasta entonces. */
export function desbloquearAudio() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (Ctx && !audioCtx) audioCtx = new Ctx();
  if (audioCtx?.state === "suspended") audioCtx.resume();
  // iOS también necesita una primera locución dentro del gesto
  if (window.speechSynthesis) {
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    window.speechSynthesis.speak(u);
  }
}

const esperar = (ms) => new Promise((r) => setTimeout(r, ms));

/** Un golpe de campana: parciales inarmónicos que se apagan poco a poco. */
function golpe(ctx, inicio, base) {
  const parciales = [
    [1, 0.5, 2.6],
    [2, 0.28, 2.0],
    [2.76, 0.2, 1.6],
    [5.4, 0.1, 1.0],
    [8.93, 0.05, 0.6],
  ];
  const master = ctx.createGain();
  master.gain.value = 0.6;
  master.connect(ctx.destination);
  for (const [ratio, gain, dur] of parciales) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = base * ratio;
    g.gain.setValueAtTime(0.0001, inicio);
    g.gain.exponentialRampToValueAtTime(gain, inicio + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, inicio + dur);
    osc.connect(g).connect(master);
    osc.start(inicio);
    osc.stop(inicio + dur + 0.05);
  }
}

/** Tilín-tilín: dos golpes. Resuelve cuando ya casi se ha apagado. */
export async function tocarCampana() {
  if (!audioCtx) return;
  if (audioCtx.state === "suspended") await audioCtx.resume();
  const t0 = audioCtx.currentTime + 0.05;
  golpe(audioCtx, t0, 1046.5);
  golpe(audioCtx, t0 + 0.35, 1318.5);
  await esperar(1600);
}

// ---- Audios grabados (opcionales) ----

const audiosExistentes = new Map(); // url -> Promise<boolean>

/** ¿Hay un audio de verdad en esa ruta? (el servidor devuelve la web si no existe) */
function existeAudio(url) {
  if (!audiosExistentes.has(url)) {
    audiosExistentes.set(
      url,
      fetch(url, { method: "HEAD" })
        .then((res) => res.ok && (res.headers.get("content-type") || "").startsWith("audio/"))
        .catch(() => false),
    );
  }
  return audiosExistentes.get(url);
}

function reproducir(url) {
  return new Promise((resolve) => {
    const audio = new Audio(url);
    const fin = setTimeout(() => resolve(false), 15000);
    audio.onended = () => {
      clearTimeout(fin);
      resolve(true);
    };
    audio.onerror = () => {
      clearTimeout(fin);
      resolve(false);
    };
    audio.play().catch(() => {
      clearTimeout(fin);
      resolve(false);
    });
  });
}

/** Intenta decir la frase con el audio grabado. true si ha sonado. */
async function decirConAudio(evento, idioma) {
  const base = `${process.env.PUBLIC_URL || ""}/campana/audio/${idioma}`;
  const intro = `${base}/propina.mp3`;
  const frase = evento.frase ? `${base}/${evento.frase}.mp3` : null;
  if (!(await existeAudio(intro)) || (frase && !(await existeAudio(frase)))) return false;
  if (!(await reproducir(intro))) return false;
  if (frase) await reproducir(frase);
  return true;
}

// ---- Voz del dispositivo ----

/** Las voces llegan tarde en algunos navegadores: espera un poco la primera vez. */
function voces() {
  const synth = window.speechSynthesis;
  if (!synth) return Promise.resolve([]);
  const ya = synth.getVoices();
  if (ya.length) return Promise.resolve(ya);
  return new Promise((resolve) => {
    const listo = () => resolve(synth.getVoices());
    synth.addEventListener?.("voiceschanged", listo, { once: true });
    setTimeout(listo, 1500);
  });
}

const vozDe = (lista, idioma) => {
  const lang = (VOCES[idioma] || idioma).toLowerCase();
  const norm = (v) => (v.lang || "").replace("_", "-").toLowerCase();
  return lista.find((v) => norm(v) === lang) || lista.find((v) => norm(v).startsWith(lang.slice(0, 2))) || null;
};

function hablar(texto, lang, voz) {
  return new Promise((resolve) => {
    if (!window.speechSynthesis || !texto) return resolve();
    const u = new SpeechSynthesisUtterance(texto);
    u.lang = lang;
    if (voz) u.voice = voz;
    u.rate = 0.95;
    const fin = setTimeout(resolve, 15000);
    u.onend = u.onerror = () => {
      clearTimeout(fin);
      resolve();
    };
    window.speechSynthesis.speak(u);
  });
}

/**
 * Dice la frase con la mejor voz que tenga el dispositivo: la del idioma del
 * negocio, o si no hay, en castellano o inglés; y si no hay ninguna, como pueda.
 */
async function decirConVoz(evento, idioma) {
  const lista = await voces();
  for (const candidato of [idioma, ...IDIOMAS_RESPALDO.filter((i) => i !== idioma)]) {
    const voz = vozDe(lista, candidato);
    if (voz) return hablar(textoCampana(evento, candidato), voz.lang, voz);
  }
  return hablar(textoCampana(evento, idioma), VOCES[idioma] || VOCES.es, null);
}

/** Campana y después la frase (audio grabado si lo hay; si no, la voz del dispositivo). */
export async function anunciar(evento, idioma) {
  await tocarCampana();
  if (await decirConAudio(evento, idioma)) return;
  await decirConVoz(evento, idioma);
}
